import { ITEM_IDS, ITEMS, OUTFIT_IDS, type ItemId, type OutfitId } from "../data/items.ts";
import { SPECIES_IDS, STARTERS, speciesById, type SpeciesId } from "../data/species.ts";
import { MAP_H, MAP_W, SPAWN, tileAt } from "../data/map.ts";
import { moveById, peekMove } from "../data/moves.ts";
import { CIRCUIT_IDS, ENCORE_IDS, TRAINER_IDS, type CircuitTrainerId, type TrainerId } from "../data/trainers.ts";
import { CLUE_IDS } from "../data/secrets.ts";
import { DAY_MS } from "../world/fx.ts";
import { hashSeed, Rng } from "../engine/rng.ts";
import { LEVEL_CAP, maxHp, mintMon, restoreAll, upgradeMoves, type Mon } from "./mon.ts";
import { emptyStats, normalizeBadges, normalizeDaily, normalizeStats, normalizeTitle, type Daily, type Stats } from "./goals.ts";

export const SAVE_KEY = "memecraft.save.v1";
export const PARTY_MAX = 6;

export interface SaveState {
  v: 1;
  seed: string;
  rng: number;
  name: string;
  money: number;
  x: number;
  y: number;
  dir: 0 | 1 | 2 | 3;
  party: Mon[];
  pc: Mon[];
  bag: Record<ItemId, number>;
  /** Sticker Pack and Frame Tint, the regional outfits owned, and the outfit worn ("" = default look). */
  cosmetics: { sticker: boolean; frame: boolean; outfits: OutfitId[]; wear: OutfitId | "base" | "" };
  seen: SpeciesId[];
  caught: SpeciesId[];
  defeatedTrainers: TrainerId[];
  circuitRematches: number;
  circuitPracticeWins: Record<CircuitTrainerId, number>;
  playMs: number;
  /** Clock time skipped by resting at kettles (added to playMs for the hour, never to play time). */
  clockShift: number;
  /** Legend hunt clues found (secrets.ts). */
  clues: string[];
  /** Post-game encores: trainer id -> the week it was last beaten. */
  encore: Partial<Record<TrainerId, number>>;
  /** Lifetime counters behind missions and badges. */
  stats: Stats;
  /** Today's missions and the login streak (null until the first day rolls). */
  daily: Daily | null;
  /** Earned badge ids and the title shown to other scouts. */
  badges: string[];
  title: string;
}

export function emptyBag(): Record<ItemId, number> {
  const bag = {} as Record<ItemId, number>;
  for (const id of Object.keys(ITEMS) as ItemId[]) bag[id] = 0;
  return bag;
}

export function newGame(seed: string, starter: SpeciesId, name = "Scout"): SaveState {
  const rng = new Rng(hashSeed(`${seed}:boot`));
  // The starter keeps its whole authored kit; early grass is the lighter side of the fight.
  const first = mintMon(rng, starter, 5, "designed");
  const bag = emptyBag();
  bag.capsule = 5;
  bag.sip = 2;
  return {
    v: 1,
    seed,
    rng: rng.state,
    name,
    money: 400,
    x: SPAWN.x,
    y: SPAWN.y,
    dir: 1,
    party: [first],
    pc: [],
    bag,
    cosmetics: { sticker: false, frame: false, outfits: [], wear: "" },
    seen: [starter],
    caught: [starter],
    defeatedTrainers: [],
    circuitRematches: 0,
    circuitPracticeWins: { rook: 0, mica: 0, nix: 0 },
    playMs: 0,
    clockShift: 0,
    clues: [],
    encore: {},
    stats: emptyStats(),
    daily: null,
    badges: [],
    title: "",
  };
}

export function persist(state: SaveState): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, Math.floor(n)));
}

function asMon(raw: unknown): Mon | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Partial<Mon>;
  if (!SPECIES_IDS.includes(m.speciesId as SpeciesId)) return null;
  const id = m.speciesId as SpeciesId;
  const sp = speciesById(id);
  const drip = m.drip ?? { hp: 0, atk: 0, def: 0, spd: 0 };
  const moves = Array.isArray(m.moves) && m.moves.length
    ? m.moves.slice(0, 4).map((slot) => {
        const def = (typeof slot?.id === "string" ? peekMove(slot.id) : null) ?? moveById("flailPost");
        return { id: def.id, pp: clamp(slot?.pp ?? def.pp, 0, def.pp) };
      })
    : sp.moves.map((mid) => ({ id: mid, pp: moveById(mid).pp }));
  const mon: Mon = {
    uid: typeof m.uid === "string" && m.uid ? m.uid : `${id}-fix`,
    speciesId: id,
    nick: typeof m.nick === "string" && m.nick.trim() ? m.nick.trim().slice(0, 12) : sp.name,
    level: clamp(m.level ?? 5, 1, LEVEL_CAP),
    exp: clamp(m.exp ?? 0, 0, 99999),
    hp: 1,
    drip: {
      hp: clamp(drip.hp ?? 0, 0, 15),
      atk: clamp(drip.atk ?? 0, 0, 15),
      def: clamp(drip.def ?? 0, 0, 15),
      spd: clamp(drip.spd ?? 0, 0, 15),
    },
    moves,
  };
  if (m.shiny === true) mon.shiny = true;
  if (mon.level === LEVEL_CAP) mon.exp = 0;
  mon.hp = clamp(m.hp ?? maxHp(mon), 0, maxHp(mon));
  upgradeMoves(mon);
  return mon;
}

/** Owned regional outfits are known ids; the worn outfit must be owned (the sticker counts once bought). */
function normalizeCosmetics(c: Partial<SaveState["cosmetics"]> | undefined): SaveState["cosmetics"] {
  const sticker = !!c?.sticker;
  const outfits = [...new Set((Array.isArray(c?.outfits) ? c!.outfits : []).filter((id): id is OutfitId =>
    typeof id === "string" && id !== "sticker" && (OUTFIT_IDS as readonly string[]).includes(id)))];
  const wear = c?.wear === "base" || (c?.wear === "sticker" && sticker) || outfits.includes(c?.wear as OutfitId) ? c!.wear! : "";
  return { sticker, frame: !!c?.frame, outfits, wear };
}

export function normalizeSave(data: unknown): SaveState | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Partial<SaveState>;
  if (d.v !== 1) return null;
  const party = (Array.isArray(d.party) ? d.party : []).map(asMon).filter((m): m is Mon => !!m);
  if (!party.length) return null;
  const pc = (Array.isArray(d.pc) ? d.pc : []).map(asMon).filter((m): m is Mon => !!m);
  const bag = emptyBag();
  if (d.bag && typeof d.bag === "object") {
    for (const id of ITEM_IDS) bag[id] = clamp((d.bag as Record<string, number>)[id] ?? 0, 0, 999);
  }
  let x = clamp(d.x ?? SPAWN.x, 0, MAP_W - 1);
  let y = clamp(d.y ?? SPAWN.y, 0, MAP_H - 1);
  if (tileAt(x, y).solid) {
    x = SPAWN.x;
    y = SPAWN.y;
  }
  const seen = (Array.isArray(d.seen) ? d.seen : []).filter((id): id is SpeciesId => SPECIES_IDS.includes(id as SpeciesId));
  const caught = (Array.isArray(d.caught) ? d.caught : []).filter((id): id is SpeciesId => SPECIES_IDS.includes(id as SpeciesId));
  if (!seen.length) seen.push(party[0]!.speciesId);
  if (!caught.length) caught.push(party[0]!.speciesId);
  const defeatedTrainers = [...new Set((Array.isArray(d.defeatedTrainers) ? d.defeatedTrainers : [])
    .filter((id): id is TrainerId => TRAINER_IDS.includes(id as TrainerId)))];
  return {
    v: 1,
    seed: typeof d.seed === "string" && d.seed ? d.seed : "drippond",
    rng: (Number.isFinite(d.rng) ? (Number(d.rng) >>> 0) : 1) || 1,
    name: typeof d.name === "string" && d.name.trim() ? d.name.trim().slice(0, 12) : "Scout",
    money: clamp(d.money ?? 0, 0, 999999),
    x,
    y,
    dir: ([0, 1, 2, 3].includes(d.dir as number) ? d.dir : 1) as 0 | 1 | 2 | 3,
    party: party.slice(0, PARTY_MAX),
    pc,
    bag,
    cosmetics: normalizeCosmetics(d.cosmetics),
    seen,
    caught,
    defeatedTrainers,
    circuitRematches: ["vell", ...CIRCUIT_IDS].every(id => defeatedTrainers.includes(id as TrainerId))
      ? clamp(d.circuitRematches ?? 0, 0, 1e9) : 0,
    circuitPracticeWins: Object.fromEntries(CIRCUIT_IDS.map(id => [id,
      defeatedTrainers.includes("vell") && defeatedTrainers.includes(id)
        ? clamp(d.circuitPracticeWins?.[id] ?? 0, 0, 1e9) : 0,
    ])) as Record<CircuitTrainerId, number>,
    playMs: clamp(d.playMs ?? 0, 0, 1e12),
    clockShift: clamp(d.clockShift ?? 0, 0, DAY_MS - 1),
    clues: [...new Set((Array.isArray(d.clues) ? d.clues : []).filter((id): id is string => CLUE_IDS.includes(id as string)))],
    encore: Object.fromEntries(Object.entries(d.encore && typeof d.encore === "object" ? d.encore : {})
      .filter(([id, week]) => (ENCORE_IDS as readonly string[]).includes(id) && defeatedTrainers.includes(id as TrainerId) && Number.isInteger(week) && (week as number) >= 0)
      .sort(([a], [b]) => a.localeCompare(b))) as Partial<Record<TrainerId, number>>,
    stats: normalizeStats(d.stats),
    daily: normalizeDaily(d.daily),
    badges: normalizeBadges(d.badges),
    title: normalizeTitle(d.title, normalizeBadges(d.badges)),
  };
}

export function loadSave(): SaveState | null {
  try {
    // The accessor itself throws when site data is blocked (sandboxed frames, some private modes).
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    return normalizeSave(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function hasSave(): boolean {
  return loadSave() !== null;
}

export function markSeen(state: SaveState, id: SpeciesId): void {
  if (!state.seen.includes(id)) state.seen.push(id);
}

export function markCaught(state: SaveState, id: SpeciesId): void {
  markSeen(state, id);
  if (!state.caught.includes(id)) state.caught.push(id);
}

export function bagCount(state: SaveState, id: ItemId): number {
  return state.bag[id] ?? 0;
}

export function bagAdd(state: SaveState, id: ItemId, n: number): void {
  state.bag[id] = Math.max(0, bagCount(state, id) + n);
}

export function stashMon(state: SaveState, mon: Mon): "party" | "pc" {
  if (state.party.length < PARTY_MAX) {
    state.party.push(mon);
    return "party";
  }
  state.pc.push(mon);
  return "pc";
}

export function clinicHeal(state: SaveState): void {
  for (const m of state.party) restoreAll(m);
}

export function living(state: SaveState): Mon[] {
  return state.party.filter((m) => m.hp > 0);
}

export function lead(state: SaveState): Mon {
  return living(state)[0] ?? state.party[0]!;
}

export function rngFrom(state: SaveState): Rng {
  return new Rng(state.rng);
}

export function commitRng(state: SaveState, rng: Rng): void {
  state.rng = rng.state;
}

export { STARTERS };
